import { buildCategoryTree, type CategoryNode } from '@/lib/categories/tree';
import type { CategoryKind, CustomCategorySummary, ParentCategory } from '@/types';

/**
 * The one option model for category pickers (Categorizar drawer on Categorias,
 * the transações recategorize drawer, and any future selector). Options are
 * flat — pickers group visually via `parentLabel` — and always carry the
 * IMMEDIATE parent's label, so a 3rd-level leaf reads under its real parent,
 * never under the root.
 */
export type CategoryOptionSource = 'pluggy' | 'custom';

export interface CategoryOption {
  id: string;
  label: string;
  iconName: string;
  /** Immediate parent's label; undefined for roots (and customs unless a group label is passed). */
  parentLabel?: string;
  source: CategoryOptionSource;
  /** User-picked color (custom categories); Pluggy options use the shared palette. */
  color?: string | null;
  /** Custom categories only — pickers badge 'neutral' ("mov. interna") options. */
  kind?: CategoryKind;
}

/**
 * Pluggy taxonomy → picker options, depth-first over the rebuilt tree: a
 * grandchild sits right under its parent, with the immediate parent's label as
 * subtitle and the nearest ancestor icon as fallback.
 */
export function buildPluggyCategoryOptions(
  parents: readonly ParentCategory[] | undefined,
): CategoryOption[] {
  const out: CategoryOption[] = [];
  const walk = (node: CategoryNode, parentLabel?: string) => {
    out.push({
      id: node.id,
      label: node.label,
      iconName: node.iconName ?? 'tag',
      parentLabel,
      source: 'pluggy',
    });
    for (const child of node.children) walk(child, node.label);
  };
  for (const root of buildCategoryTree(parents).roots) walk(root);
  return out;
}

/**
 * A user category as a picker option. `parentLabel` is the picker's group
 * caption — pass `null` for none (an explicit `undefined` would trigger the
 * default).
 */
export function optionFromCustomCategory(
  custom: Pick<CustomCategorySummary, 'id' | 'name' | 'icon'> & {
    color?: string | null;
    kind?: CategoryKind;
  },
  parentLabel: string | null = 'Sua categoria',
): CategoryOption {
  return {
    id: custom.id,
    label: custom.name,
    iconName: custom.icon ?? 'tag',
    parentLabel: parentLabel ?? undefined,
    source: 'custom',
    color: custom.color,
    kind: custom.kind,
  };
}
